import { BuildingOfficeIcon, CalendarDaysIcon, ClockIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { Badge, Box, Divider, Group, Paper, Stack, Text, useMantineColorScheme } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { OpportunityListItem } from '../../../models/opportunity.model';
import { STATUS_COLOR, STATUS_LABEL, TYPE_ACCENT } from '../constants/opportunity.constants';
import { OpportunityTypeBadge } from './OpportunityTypeBadge';

interface Props {
  opportunity: OpportunityListItem;
}

const formatName = (first: string | null, last: string | null) =>
  [first, last].filter(Boolean).join(' ') || '—';

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const MAX_POSITIONS = 5;

const iconStyle = { width: 13, height: 13, flexShrink: 0 as const, marginTop: 1 };

export const OpportunityCard = ({ opportunity }: Props) => {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const createdBy = formatName(opportunity.createdByFirstName, opportunity.createdByLastName);
  const visiblePositions = opportunity.positionSummaries.slice(0, MAX_POSITIONS);
  const hiddenCount = opportunity.positionSummaries.length - visiblePositions.length;

  return (
    <Paper
      withBorder
      radius="md"
      onClick={() => navigate(ROUTES.OPPORTUNITY_DETAIL(opportunity.id))}
      style={{
        cursor: 'pointer',
        overflow: 'hidden',
        borderTop: `3px solid ${TYPE_ACCENT[opportunity.opportunityType]}`,
        backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'white',
        borderColor: isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)',
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
        display: 'flex',
        flexDirection: 'column',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.boxShadow = '0 6px 20px rgba(0,0,0,0.10)';
        el.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.boxShadow = '';
        el.style.transform = '';
      }}
    >
      {/* ── Main body ──────────────────────────────────────────────────── */}
      <Box p="md" style={{ flex: 1 }}>
        <Stack gap="sm">
          {/* Type + status badges */}
          <Group justify="space-between" align="center">
            <OpportunityTypeBadge type={opportunity.opportunityType} />
            <Badge color={STATUS_COLOR[opportunity.opportunityStatus]} variant="light" size="sm">
              {STATUS_LABEL[opportunity.opportunityStatus]}
            </Badge>
          </Group>

          {/* Title */}
          <Text fw={700} size="md" lineClamp={2} lh={1.3}>
            {opportunity.title}
          </Text>

          {/* Description */}
          {opportunity.description ? (
            <Text size="sm" c="dimmed" lineClamp={3} lh={1.6}>
              {opportunity.description}
            </Text>
          ) : (
            <Text size="sm" c="dimmed" fs="italic">No description provided.</Text>
          )}

          {/* Scheduling & meta */}
          <Stack gap={5} mt={2}>
            {opportunity.requiredAvailabilityDate && (
              <Group gap={6} align="flex-start" wrap="nowrap">
                <CalendarDaysIcon style={iconStyle} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  Available {formatDate(opportunity.requiredAvailabilityDate)}
                </Text>
              </Group>
            )}
            {opportunity.timeCommitmentPercent != null && (
              <Group gap={6} align="flex-start" wrap="nowrap">
                <ClockIcon style={iconStyle} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  {opportunity.timeCommitmentPercent}% time commitment
                </Text>
              </Group>
            )}
            {opportunity.targetOfficeName && (
              <Group gap={6} align="flex-start" wrap="nowrap">
                <BuildingOfficeIcon style={iconStyle} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  {opportunity.targetOfficeName}
                </Text>
              </Group>
            )}
            <Group gap={6} align="flex-start" wrap="nowrap">
              <UserCircleIcon style={iconStyle} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed">
                {createdBy}
              </Text>
            </Group>
          </Stack>
        </Stack>
      </Box>

      {/* ── Positions footer ───────────────────────────────────────────── */}
      {visiblePositions.length > 0 && (
        <>
          <Divider />
          <Box
            px="md"
            py="xs"
            style={{
              backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-gray-0)',
            }}
          >
            <Stack gap={5}>
              <Text
                size="xs"
                tt="uppercase"
                fw={600}
                c="dimmed"
                style={{ letterSpacing: '0.06em' }}
              >
                Positions
              </Text>
              <Group gap="xs" wrap="wrap">
                {visiblePositions.map((pos) => (
                  <Badge
                    key={pos.id}
                    size="xs"
                    variant="outline"
                    radius="sm"
                    style={{
                      fontWeight: 400,
                      borderColor: isDark
                        ? 'var(--mantine-color-dark-3)'
                        : 'var(--mantine-color-gray-4)',
                      color: isDark
                        ? 'var(--mantine-color-gray-5)'
                        : 'var(--mantine-color-gray-6)',
                    }}
                  >
                    {pos.title}
                  </Badge>
                ))}
                {hiddenCount > 0 && (
                  <Text size="xs" c="dimmed">
                    +{hiddenCount} more
                  </Text>
                )}
              </Group>
            </Stack>
          </Box>
        </>
      )}
    </Paper>
  );
};
